import { useEffect, useRef } from "react";
import logger from "../utils/logger";
import * as appStorage from "../utils/appIndexedDB";
import { showReplyNotification } from "../utils/notifications";

function extractTextFromMessage(message) {
  if (!message?.body) return "";
  try {
    const body = JSON.parse(message.body);
    return body.message || body.text || body.content || body.raw || message.body || "";
  } catch (e) {
    // 非 JSON 就當純字串
    return message.body || "";
  }
}

/**
 * 方案三：只回填「轉接成功之後」且 room_id 為 null 的訊息。
 * 從第一則 hasTransferToHumanAgent 的訊息之後開始回填，避免改動轉接前的歷史。
 * @param {Array} messages 目前對話訊息列表
 * @param {string} roomId 要回填的 roomId
 * @returns {Array} 回填後的訊息列表（新陣列）
 */
function backfillRoomIdFromTransferPoint(messages, roomId) {
  if (!messages?.length || !roomId) return messages ?? [];
  const transferIndex = messages.findIndex((m) => m.hasTransferToHumanAgent === true);
  const startIndex = transferIndex === -1 ? 0 : transferIndex;
  return messages.map((m, i) =>
    i >= startIndex && !m.room_id ? { ...m, room_id: roomId } : m
  );
}

/**
 * 訂閱 /web/user/{userId}/queue/customer-service，收到訊息後寫入 chatHistory
 *
 * @param {Function} [onAiStreamEvent] 收到 AI 串流事件（type 為 AI_STREAM_DELTA / AI_STREAM_DONE）時的回呼，
 *   由呼叫端（App.js 經由 useChat）提供；此 hook 本身不處理串流緩衝，只負責分流。
 */
export function useCustomerServiceSubscription({
  enabled,
  stompClient,
  connected,
  authUser,
  chatHistory,
  isHumanAgent = false,
  disableWs,
  onAiStreamEvent,
}) {
  // 使用 ref 存儲最新的 chatHistory 和 isHumanAgent，避免因它們的變化導致重複訂閱
  const chatHistoryRef = useRef(chatHistory);
  const isHumanAgentRef = useRef(isHumanAgent);
  // AI 串流事件回呼也存成 ref，理由同上：避免因它變化而重新訂閱
  const onAiStreamEventRef = useRef(onAiStreamEvent);

  // 保持 ref 為最新值
  useEffect(() => {
    chatHistoryRef.current = chatHistory;
    isHumanAgentRef.current = isHumanAgent;
    onAiStreamEventRef.current = onAiStreamEvent;
  }, [chatHistory, isHumanAgent, onAiStreamEvent]);

  useEffect(() => {
    if (!enabled || !connected || !stompClient) return;

    const userId = authUser?.id || authUser?.userId || authUser?.username;
    if (!userId) {
      logger.warn("WebSocket", "無法訂閱 customer-service，缺少 userId");
      return;
    }

    const queuePath = `/web/user/${userId}/queue/customer-service`;
    logger.info("WebSocket", "開始訂閱 customer-service 佇列", { userId, queuePath, isHumanAgent: isHumanAgentRef.current });

    const subscription = stompClient.subscribe(queuePath, (message) => {
      try {
        logger.info("WebSocket", "收到 customer-service 訊息", {
          queuePath,
          rawBody: message.body,
          headers: message.headers,
          command: message.command,
        });

        // 解析 body 取得 roomId 與後續判斷用
        let body = null;
        try {
          body = JSON.parse(message.body);
        } catch (e) {
          logger.warn("WebSocket", "收到 customer-service 訊息但內容非 JSON", {
            rawBody: message.body,
          });
        }
        const roomId = body?.roomId ?? null;
        const messageId = body?.messageId ?? null;

        // AI 串流事件（delta/done/clear）：導向 useChat 的串流處理邏輯，不建立正式訊息項目，
        // 必須放在 ROOM_ASSIGNED／一般文字訊息判斷之前，避免 done 事件的 text 欄位被誤判為一則完整訊息
        if (body?.type === "AI_STREAM_DELTA" || body?.type === "AI_STREAM_DONE" || body?.type === "AI_STREAM_CLEAR") {
          onAiStreamEventRef.current?.(body);
          return;
        }

        // 方案一：轉接完成後後端主動推送 ROOM_ASSIGNED，收到後回填當前對話的 room_id（範圍同方案三：轉接點之後）
        if (body?.type === "ROOM_ASSIGNED") {
          const assignedRoomId = body?.roomId ?? null;
          if (!assignedRoomId) return;
          const currentChatHistory = chatHistoryRef.current;
          const currentChatId = currentChatHistory?.currentChatId;
          if (!currentChatId) {
            logger.warn("WebSocket", "收到 ROOM_ASSIGNED 但沒有當前 chatId", { roomId: assignedRoomId });
            return;
          }
          const existing = currentChatHistory.getCurrentMessages() ?? [];
          const backfilled = backfillRoomIdFromTransferPoint(existing, assignedRoomId);
          currentChatHistory.updateChatMessages(currentChatId, backfilled);
          currentChatHistory.notifyWsMessageReceived?.();
          logger.success("WebSocket", "已收到 ROOM_ASSIGNED 並回填 roomId", {
            roomId: assignedRoomId,
            chatId: currentChatId,
            backfilledCount: backfilled.filter((m) => m.room_id === assignedRoomId).length,
          });
          return;
        }

        const text = extractTextFromMessage(message);

        if (!text) {
          logger.warn("WebSocket", "收到 customer-service 訊息但內容為空", {
            rawBody: message.body,
          });
          return;
        }

        // 使用 ref 獲取最新的 chatHistory
        const currentChatHistory = chatHistoryRef.current;
        const currentChatId = currentChatHistory?.currentChatId;
        if (!currentChatId) {
          logger.warn("WebSocket", "收到訊息但沒有當前 chatId", { textLength: text.length });
          return;
        }

        // 改用 append 模式：避免 server 快速連發時，callback 取得舊 snapshot 而覆蓋丟訊息
        const incoming = {
          sender: "rocketChat",
          text,
          ...(messageId ? { id: messageId } : {}),
          ...(roomId != null ? { room_id: roomId } : {}),
          ...(isHumanAgentRef.current ? { isHumanAgent: true, isHuman: true } : { isHuman: false }),
        };

        currentChatHistory.updateChatMessages(currentChatId, [incoming], {
          append: true,
        });
        currentChatHistory.notifyWsMessageReceived?.();
        
        logger.info("WebSocket", "document.visibilityState", { visibilityState: document.visibilityState });
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          logger.info("showReplyNotification", text)
          showReplyNotification(text);
        }

        logger.success("WebSocket", "已將訊息加入對話", {
          chatId: currentChatId,
          isHumanAgent: isHumanAgentRef.current,
          messageId,
          preview: text.slice(0, 100) + (text.length > 100 ? "..." : ""),
        });

        // 檢查是否為客服會話結束訊息
        if (body?.type === "CUSTOMER_SERVICE_ENDED") {
          logger.info("WebSocket", "更新 nowIsWebSocket 至本地儲存", {
            nowIsWebSocket: "false",
          });
          void appStorage.setItem("nowIsWebSocket", "false");
          logger.info("WebSocket", "nowIsWebSocket 已更新至本地儲存", {
            nowIsWebSocket: "false",
          });

          logger.info("WebSocket", "收到客服會話結束訊息，關閉 WebSocket!", {
            message: body.message,
            timestamp: body.timestamp,
          });
          // 恢復原本沒有開啟 WebSocket 的狀態
          if (disableWs) {
            disableWs();
          }

          return;
        }
      } catch (err) {
        logger.error("WebSocket", "處理 customer-service 訊息失敗", {
          error: err.message,
          stack: err.stack,
        });
      }
    });

    return () => {
      subscription?.unsubscribe();
      logger.info("WebSocket", "取消訂閱 customer-service 佇列", { isHumanAgent: isHumanAgentRef.current });
    };
  }, [enabled, connected, stompClient, authUser?.id, authUser?.userId, authUser?.username]);
}
